import { CurrentUser, useCurrentUser, useToastProvider } from '@qualio/ui-components';
import supplierApi from 'api/supplier.api';
import { useTranslation } from 'react-i18next';
import { UseMutateFunction, useMutation, useQuery } from 'react-query';
import { Audit } from 'types';

const prepareAuditData = (audit: Audit, currentUser: CurrentUser, isDelete: boolean): Audit => {
  const newAudit: any = { ...audit };

  newAudit.name = newAudit.auditName || newAudit.name;
  newAudit.user = { ...currentUser, id: currentUser.userId };
  newAudit.endDate = newAudit.endDate || undefined;
  newAudit.notes = newAudit.notes || undefined;

  if (isDelete) {
    newAudit.status = 'ARCHIVED';
  }

  if (newAudit.documents?.filter((document: any) => document.file).length) {
    newAudit.documents?.forEach((document: any) => {
      document.file.fileName = document.file.fileName || document.file.name;
      if (!document.file.description) {
        delete document.file.description;
      }
    });
  } else {
    newAudit.documents = [];
  }

  newAudit.auditType = {
    title: newAudit?.auditType?.label,
    id: newAudit?.auditType?.value,
  };

  return newAudit;
};

type UseAuditQueryType = {
  audits?: Audit[];
  isAuditsLoading: boolean;
  refetchAudits: () => void;
};

export const useAuditsQuery = (supplierId: string): UseAuditQueryType => {
  const { companyId } = useCurrentUser();

  const {
    data: audits,
    isLoading: isAuditsLoading,
    refetch: refetchAudits,
  } = useQuery(`supplier-audits-${supplierId}`, () => supplierApi.getAuditsBySupplierId(companyId, supplierId));

  return { audits, isAuditsLoading, refetchAudits };
};

type UseAuditMutationType = {
  createAudit: UseMutateFunction<
    Audit,
    unknown,
    {
      auditState: Audit;
      supplierId: string;
    },
    unknown
  >;
  isAuditLoading: boolean;
};

export const useAuditMutation = (onSuccess: () => void): UseAuditMutationType => {
  const { t } = useTranslation();
  const { companyId } = useCurrentUser();
  const { showToast } = useToastProvider();
  const user = useCurrentUser();

  const { mutate: createAudit, isLoading: isAuditLoading } = useMutation(
    ({ auditState, supplierId }: { auditState: Audit; supplierId: string }) => {
      const preparedAuditData = prepareAuditData(auditState, user, false);
      return supplierApi.createAudit(companyId, supplierId, preparedAuditData);
    },
    {
      onSuccess: () => {
        showToast({ title: t('toastMessages.audit.added'), status: 'success', 'data-cy': 'toast-audit-added' });
        onSuccess();
      },
      onError: () => {
        showToast({
          title: t('toastMessages.audit.creationFailed'),
          description: t('toastMessages.audit.creationFailedDescription'),
          status: 'error',
        });
      },
    },
  );

  return { createAudit, isAuditLoading };
};

type UseAuditEditMutationType = {
  editAudit: UseMutateFunction<
    Audit,
    unknown,
    {
      auditState: Audit;
      supplierId: string;
      originalAudit: Audit;
    },
    unknown
  >;
  isAuditEditLoading: boolean;
};

export const useAuditEditMutation = (onSuccess: () => void): UseAuditEditMutationType => {
  const { t } = useTranslation();
  const { companyId } = useCurrentUser();
  const { showToast } = useToastProvider();
  const user = useCurrentUser();

  const { mutate: editAudit, isLoading: isAuditEditLoading } = useMutation(
    ({ auditState, supplierId, originalAudit }: { auditState: Audit; supplierId: string; originalAudit: Audit }) => {
      const preparedAuditData = prepareAuditData(auditState, user, false);
      const fullAudit: Audit = {
        ...originalAudit,
        ...preparedAuditData,
        audit: originalAudit.audit,
        status: originalAudit.status,
      };
      return supplierApi.updateAudit(companyId, supplierId, fullAudit);
    },
    {
      onSuccess: () => {
        showToast({ title: t('toastMessages.audit.updated'), status: 'success' });
        onSuccess();
      },
      onError: () => {
        showToast({
          title: t('toastMessages.audit.updateFailed'),
          description: t('toastMessages.audit.updateFailedDescription'),
          status: 'error',
        });
      },
    },
  );

  return { editAudit, isAuditEditLoading };
};

type UseAuditDeleteMutateType = {
  deleteAudit: UseMutateFunction<
    Audit,
    unknown,
    {
      auditState: Audit;
      supplierId: string;
    },
    unknown
  >;
  isAuditDeleteLoading: boolean;
};

export const useAuditDeleteMutate = (onSuccess: () => void): UseAuditDeleteMutateType => {
  const { t } = useTranslation();
  const { companyId } = useCurrentUser();
  const { showToast } = useToastProvider();
  const user = useCurrentUser();

  const { mutateAsync: deleteAudit, isLoading: isAuditDeleteLoading } = useMutation(
    ({ auditState, supplierId }: { auditState: Audit; supplierId: string }) => {
      const preparedAuditData = prepareAuditData(auditState, user, true);
      return supplierApi.updateAudit(companyId, supplierId as string, preparedAuditData);
    },
    {
      onSuccess: () => {
        onSuccess();
        showToast({
          title: t('toastMessages.audit.deleted'),
          status: 'success',
        });
      },
      onError: () => {
        showToast({
          title: t('toastMessages.audit.deletionFailed'),
          description: t('toastMessages.audit.deletionFailedDescription'),
          status: 'error',
        });
      },
    },
  );
  return { deleteAudit, isAuditDeleteLoading };
};
